import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faFileExcel, faCircleNotch, faCheckCircle, 
    faExclamationTriangle, faTimes, faDownload,
    faChevronDown, faChevronUp, faTrashAlt
} from '@fortawesome/free-solid-svg-icons';
import queueExcel from '../../../../services/queueExcel';

/**
 * DownloadSpool — Floating widget to monitor and manage Excel downloads.
 * Connects to the queueExcel service to provide real-time updates.
 */
const DownloadSpool = () => {
    const [queue, setQueue] = useState([]);
    const [isExpanded, setIsExpanded] = useState(false);
    const [hasNew, setHasNew] = useState(false);

    useEffect(() => {
        // Subscribe to queue changes
        queueExcel.setListen((newQueue) => {
            setQueue([...newQueue]);
            if (newQueue.length > 0) {
                setHasNew(true);
            }
        });

        // Initialize with current queue
        setQueue([...queueExcel.queue]);
    }, []);

    const activeCount = queue.filter(q => q.processing || q.pending).length;
    const completedCount = queue.filter(q => q.completed).length;

    if (queue.length === 0) return null;

    const handleClearAll = (e) => {
        e.stopPropagation();
        queueExcel.deleteAll();
    };

    const handleRemoveItem = (e, token) => {
        e.stopPropagation();
        queueExcel.deleteItem(token);
    };

    return (
        <div className="sa-spool-container">
            <AnimatePresence>
                {isExpanded && (
                    <motion.div 
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.95 }}
                        className="sa-spool-panel"
                    >
                        <div className="sa-spool-header">
                            <div className="d-flex align-items-center gap-2">
                                <FontAwesomeIcon icon={faFileExcel} className="text-success" />
                                <span className="fw-bold">Cola de Descargas</span>
                                <span className="sa-badge-count">{queue.length}</span>
                            </div>
                            <button className="sa-spool-close" onClick={() => setIsExpanded(false)}>
                                <FontAwesomeIcon icon={faChevronDown} />
                            </button>
                        </div>

                        <div className="sa-spool-list">
                            {queue.map((item, idx) => (
                                <div key={item.tokenQueue || idx} className={`sa-spool-item ${item.completed ? 'completed' : ''} ${item.fail ? 'failed' : ''}`}>
                                    <div className="sa-spool-item-icon">
                                        {item.processing ? (
                                            <FontAwesomeIcon icon={faCircleNotch} spin className="text-primary" />
                                        ) : item.completed ? (
                                            <FontAwesomeIcon icon={faCheckCircle} className="text-success" />
                                        ) : item.fail ? (
                                            <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger" />
                                        ) : (
                                            <FontAwesomeIcon icon={faCircleNotch} className="text-muted opacity-50" />
                                        )}
                                    </div>
                                    
                                    <div className="sa-spool-item-info">
                                        <div className="sa-spool-item-name">{item.nombre || 'Reporte'}</div>
                                        <div className="sa-spool-item-status">
                                            {item.processing 
                                                ? `Procesando... ${queueExcel.porcent(item)}` 
                                                : item.completed 
                                                    ? 'Completado' 
                                                    : item.fail 
                                                        ? 'Error al generar' 
                                                        : 'En espera...'}
                                        </div>
                                        {item.processing && (
                                            <div className="sa-spool-progress-bar">
                                                <motion.div 
                                                    className="sa-spool-progress-fill"
                                                    initial={{ width: 0 }}
                                                    animate={{ width: queueExcel.porcent(item) }}
                                                />
                                            </div>
                                        )}
                                    </div>

                                    <div className="sa-spool-item-actions">
                                        {item.completed && (
                                            <button 
                                                className="sa-action-btn success" 
                                                title="Re-descargar"
                                                onClick={() => queueExcel.activateItem(item.tokenQueue)}
                                            >
                                                <FontAwesomeIcon icon={faDownload} />
                                            </button>
                                        )}
                                        <button 
                                            className="sa-action-btn danger" 
                                            title="Eliminar"
                                            onClick={(e) => handleRemoveItem(e, item.tokenQueue)}
                                        >
                                            <FontAwesomeIcon icon={faTrashAlt} />
                                        </button>
                                    </div>
                                </div>
                            )).reverse()}
                        </div>

                        <div className="sa-spool-footer">
                            <button className="sa-btn-link" onClick={handleClearAll}>
                                Limpiar todo
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <motion.button 
                className={`sa-spool-trigger ${activeCount > 0 ? 'processing' : ''} ${hasNew && !isExpanded ? 'has-new' : ''}`}
                onClick={() => {
                    setIsExpanded(!isExpanded);
                    setHasNew(false);
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
            >
                <div className="d-flex align-items-center gap-2">
                    {activeCount > 0 ? (
                        <FontAwesomeIcon icon={faCircleNotch} spin />
                    ) : (
                        <FontAwesomeIcon icon={faFileExcel} />
                    )}
                    {!isExpanded && (
                        <span>
                            {activeCount > 0 ? `Descargando (${activeCount})` : 'Descargas'}
                        </span>
                    )}
                </div>
                {completedCount > 0 && !isExpanded && (
                    <span className="sa-spool-badge">{completedCount}</span>
                )}
            </motion.button>
        </div>
    );
};

export default DownloadSpool;
